import { AppButton } from '@clickguard/design-system'
import { useAuth0 } from '@auth0/auth0-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import { useSearchParams } from 'react-router-dom'
import { Users } from '../../../services/user'
import { parseError } from '../../../utils/errors'
import { useUserStore } from '../../../store'
import { useStores } from '../../../hooks'
import {
	setPendingInviteCode,
	getPendingInviteCode,
	isValidInviteCode
} from '../../../utils/invite-storage'
import { OnboardingLayout } from '../../../components/onboarding-layout'
import { trackAccountCreation1 } from '../../../utils/ga4-events'

export const EmailVerifiedView = () => {
	const [search] = useSearchParams()
	const otpCode = search.get('otp')
	const inviteCodeFromUrl = search.get('code')
	const user = useUserStore((state) => state.user)
	const verifyEmailByCode = useUserStore((state) => state.verifyEmailByCode)
	const [status, setStatus] = useState<'idel' | 'loading' | 'success'>('idel')
	const [digits, setDigits] = useState<string[]>(['', '', '', '', '', ''])
	const [error, setError] = useState('')
	const [resendTimer, setResendTimer] = useState(0)
	const [isResendDisabled, setIsResendDisabled] = useState(false)
	const { logout, loginWithRedirect } = useAuth0()
	const { adminStore } = useStores()
	const inputRefs = useRef<(HTMLInputElement | null)[]>([])

	const code = digits.join('')

	useEffect(() => {
		if (isValidInviteCode(inviteCodeFromUrl)) {
			setPendingInviteCode(inviteCodeFromUrl)
		}
	}, [inviteCodeFromUrl])

	const formatTimer = (seconds: number) => {
		const minutes = Math.floor(seconds / 60)
		const remainingSeconds = seconds % 60
		return `${minutes.toString().padStart(2, '0')}:${remainingSeconds
			.toString()
			.padStart(2, '0')}`
	}

	const verifyEmail = useCallback(async () => {
		const currentCode = digits.join('')
		try {
			if (currentCode.length !== 6) {
				setError('Invalid verification code. Please try again.')
				return
			}
			setStatus('loading')
			await verifyEmailByCode(currentCode)
			const currentUser = user || useUserStore.getState().user
			if (currentUser?.id) {
				trackAccountCreation1({
					user_id: currentUser.id,
					email: currentUser.email,
					name: currentUser.name
				})
			}
			toast.success('Email verified successfully')

			const pendingInviteCode = getPendingInviteCode()

			setTimeout(() => {
				if (pendingInviteCode) {
					loginWithRedirect({
						screen_hint: 'email_verified',
						appState: {
							returnTo: `/join-workspace?code=${pendingInviteCode}`
						}
					})
				} else {
					loginWithRedirect({
						screen_hint: 'email_verified'
					})
				}
			}, 500)
			setStatus('success')
		} catch (e: any) {
			setError(e?.message || 'Invalid verification code. Please try again.')
			setStatus('idel')
		}
	}, [digits, verifyEmailByCode, user, loginWithRedirect])

	const handleDigitChange = (index: number, value: string) => {
		if (value.length > 1) {
			value = value.slice(-1)
		}
		if (value && !/^\d$/.test(value)) return

		setError('')
		const newDigits = [...digits]
		newDigits[index] = value
		setDigits(newDigits)

		if (value && index < 5) {
			inputRefs.current[index + 1]?.focus()
		}
	}

	const handleKeyDown = (
		index: number,
		e: React.KeyboardEvent<HTMLInputElement>
	) => {
		if (e.key === 'Backspace' && !digits[index] && index > 0) {
			inputRefs.current[index - 1]?.focus()
		}
	}

	const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
		e.preventDefault()
		const pasted = e.clipboardData
			.getData('text')
			.replace(/\D/g, '')
			.slice(0, 6)
		if (pasted.length === 0) return

		setError('')
		const newDigits = [...digits]
		for (let i = 0; i < 6; i++) {
			newDigits[i] = pasted[i] || ''
		}
		setDigits(newDigits)

		const focusIndex = Math.min(pasted.length, 5)
		inputRefs.current[focusIndex]?.focus()
	}

	const resend = async () => {
		if (!user) {
			return
		}
		if (user.email_verified) {
			return toast.error('Email already verified')
		}

		try {
			await Users.resendVerificationEmail({
				email: user.email
			})
			toast.success('Verification link sent')
			const endTime = Date.now() + 120 * 1000
			localStorage.setItem('resendTimerEnd', endTime.toString())
			setResendTimer(120)
			setIsResendDisabled(true)
		} catch (err: any) {
			toast.error(
				err?.response?.data?.message
					? parseError(err)
					: "Couldn't send verification link"
			)
		}
	}

	useEffect(() => {
		if (code.length === 6 && status !== 'loading') {
			verifyEmail()
		}
	}, [code])

	useEffect(() => {
		if (otpCode) {
			setTimeout(() => {
				const otpDigits = otpCode.replace(/\D/g, '').slice(0, 6).split('')
				const newDigits = ['', '', '', '', '', '']
				otpDigits.forEach((d, i) => {
					newDigits[i] = d
				})
				setDigits(newDigits)
			}, 1000)
		}
	}, [otpCode])

	useEffect(() => {
		const storedEndTime = localStorage.getItem('resendTimerEnd')
		if (storedEndTime) {
			const endTime = parseInt(storedEndTime)
			const currentTime = Date.now()
			const remainingTime = Math.max(
				0,
				Math.ceil((endTime - currentTime) / 1000)
			)

			if (remainingTime > 0) {
				setResendTimer(remainingTime)
				setIsResendDisabled(true)
			} else {
				localStorage.removeItem('resendTimerEnd')
			}
		}
	}, [])

	useEffect(() => {
		let interval: NodeJS.Timeout | null = null

		if (resendTimer > 0) {
			interval = setInterval(() => {
				setResendTimer((prevTimer) => {
					if (prevTimer <= 1) {
						setIsResendDisabled(false)
						localStorage.removeItem('resendTimerEnd')
						return 0
					}
					return prevTimer - 1
				})
			}, 1000)
		}

		return () => {
			if (interval) {
				clearInterval(interval)
			}
		}
	}, [resendTimer])

	const getBorderColor = (index: number) => {
		if (status === 'success') return 'border-[#17B26A]'
		if (error) return 'border-[#F04438]'
		if (digits[index]) return 'border-[var(--border-brand,#554BFD)]'
		return 'border-t-gray-input'
	}

	return (
		<OnboardingLayout currentStep={2} showStepper>
			<div
				className="w-full max-w-[556px] bg-white rounded-3xl md:px-[32px] md:py-[32px] px-[24px] py-[24px]"
				style={{
					boxShadow:
						'0px 12px 16px 0px rgba(16,24,40,0.08), 0px 4px 6px 0px rgba(16,24,40,0.03)'
				}}
			>
				<div className="flex flex-col">
					<h2 className="text-[30px] mb-1 font-medium text-t-default leading-[36px]">
						Verify your email
					</h2>
					<p className="text-t-checkbox-unactive text-lg leading-5 mb-6 font-normal">
						We sent a 6-digit code to {user?.email}
					</p>
				</div>

				<form className="mt-0" onSubmit={(e) => e.preventDefault()}>
					<div className="flex flex-col items-center justify-center">
						{/* The boxes share the row with the Verify button, so they span the
						    same width: six equal squares, no fixed box width. */}
						<div className="flex gap-3 mb-3 w-full">
							{digits.map((digit, index) => (
								<input
									key={index}
									ref={(el) => {
										inputRefs.current[index] = el
									}}
									className={`flex-1 min-w-0 aspect-square text-center md:text-2xl text-md font-medium text-t-default rounded-lg bg-transparent ${getBorderColor(
										index
									)} focus:outline-none focus:border-[var(--border-brand,#554BFD)] transition-colors`}
									style={{ borderWidth: '1.5px' }}
									inputMode="numeric"
									maxLength={1}
									type="text"
									value={digit}
									onChange={(e) => handleDigitChange(index, e.target.value)}
									onKeyDown={(e) => handleKeyDown(index, e)}
									onPaste={index === 0 ? handlePaste : undefined}
								/>
							))}
						</div>

						{status === 'success' && (
							<p className="text-[#22C55E] text-base font-normal w-full text-left mb-3">
								Email verified successfully
							</p>
						)}

						{error && (
							<p className="text-[#F04438] text-base font-normal w-full text-left mb-3">
								{error}
							</p>
						)}

						<p className="text-base text-t-default mb-6 w-full text-center">
							Didn&apos;t get a code?{' '}
							<button
								className={`${
									isResendDisabled
										? 'text-t-gray-hold cursor-not-allowed'
										: 'font-medium text-[color:var(--text-link,#554BFD)]'
								}`}
								disabled={status === 'loading' || isResendDisabled}
								onClick={resend}
								type="button"
							>
								{isResendDisabled
									? `Resend in ${formatTimer(resendTimer)}`
									: 'Resend'}
							</button>
						</p>

						<AppButton
							variant="marketing"
							size="lg"
							fullWidth
							label="Verify"
							isLoading={status === 'loading'}
							loader="dots"
							disabled={
								code.length !== 6 ||
								status === 'loading' ||
								status === 'success'
							}
							onClick={verifyEmail}
						/>
					</div>
				</form>
			</div>
		</OnboardingLayout>
	)
}
